import React from 'react';
import { Building, CheckCircle } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';

const Projects = () => {
  const clients = [
    { 
      name: 'DLF', 
      logo: 'https://customer-assets.emergentagent.com/job_ee7a6fc9-ec67-452c-823a-7c8341086989/artifacts/wi6cgyn3_DLF.png' 
    },
    { 
      name: 'Tata Consultancy Services', 
      logo: 'https://customer-assets.emergentagent.com/job_ee7a6fc9-ec67-452c-823a-7c8341086989/artifacts/zf0r6krv_TATA_Consultancy_Services_Logo_blue.svg_.png'
    },
    { 
      name: 'Radisson', 
      logo: 'https://customer-assets.emergentagent.com/job_ee7a6fc9-ec67-452c-823a-7c8341086989/artifacts/8uw31i9q_Radisson.png' 
    },
    { 
      name: 'Emaar', 
      logo: 'https://customer-assets.emergentagent.com/job_ee7a6fc9-ec67-452c-823a-7c8341086989/artifacts/qs7xytxd_emaar%20logo.gif' 
    },
    { 
      name: 'Genpact', 
      logo: 'https://customer-assets.emergentagent.com/job_ee7a6fc9-ec67-452c-823a-7c8341086989/artifacts/f7uj1cpb_1200px-Genpact_logo.svg.png' 
    },
    { 
      name: 'Ireo', 
      logo: 'https://customer-assets.emergentagent.com/job_ee7a6fc9-ec67-452c-823a-7c8341086989/artifacts/05jk13vs_IREO_logo.png' 
    }
  ];

  const projectTypes = [
    {
      title: 'Office Fit-Outs',
      description: 'Complete electrical engineering solutions for corporate office spaces',
      image: 'https://images.unsplash.com/photo-1685946973834-81f1c465ec0c?w=500&q=70&auto=format'
    },
    {
      title: 'Industrial Installations',
      description: 'Large-scale electrical installations for industrial facilities',
      image: 'https://images.unsplash.com/photo-1738918929491-3c102ce11c8a?w=500&q=70&auto=format'
    },
    {
      title: 'Infrastructure Projects',
      description: 'Substations, transmission lines, and critical infrastructure development',
      image: 'https://images.unsplash.com/photo-1487491424367-7571f9afbb30?w=500&q=70&auto=format'
    },
    {
      title: 'Data Centers',
      description: 'Total data center solutions with redundant power and cooling systems',
      image: 'https://images.unsplash.com/photo-1738918937796-743064feefa1?w=500&q=70&auto=format'
    }
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Hero Section */}
      <section className="py-20 bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <h1 className="text-5xl md:text-6xl font-bold mb-6">Our Projects</h1>
          <p className="text-xl text-slate-300 max-w-3xl mx-auto">
            Successfully delivered projects across India for leading corporations and government sectors
          </p>
        </div>
      </section>

      {/* Project Types */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4 text-slate-900">Project Portfolio</h2>
            <p className="text-xl text-slate-600">Diverse range of engineering solutions delivered with excellence</p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {projectTypes.map((project, index) => (
              <Card key={index} className="border-none shadow-lg hover:shadow-xl transition-shadow duration-300 overflow-hidden">
                <div className="h-64 overflow-hidden">
                  <img
                    src={project.image}
                    alt={project.title}
                    className="w-full h-full object-cover hover:scale-110 transition-transform duration-300"
                    loading="lazy"
                  />
                </div>
                <CardContent className="p-6">
                  <Building className="h-10 w-10 text-blue-600 mb-4" />
                  <h3 className="text-2xl font-bold mb-3 text-slate-900">{project.title}</h3>
                  <p className="text-slate-600">{project.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Clients Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4 text-slate-900">Our Clients</h2>
            <p className="text-xl text-slate-600">Trusted by leading organizations across India</p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-8">
            {clients.map((client, index) => (
              <Card key={index} className="border-none shadow-md hover:shadow-lg transition-shadow duration-300">
                <CardContent className="p-8 text-center flex items-center justify-center min-h-[150px]">
                  {client.logo ? (
                    <img 
                      src={client.logo} 
                      alt={client.name}
                      className="max-h-20 max-w-full w-auto object-contain"
                    />
                  ) : (
                    <p className="font-semibold text-slate-900 text-lg">{client.name}</p>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-blue-600 text-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl font-bold mb-6">Ready to Start Your Project?</h2>
          <p className="text-xl mb-8 text-blue-100">
            Let's discuss how we can deliver exceptional engineering solutions for your next project
          </p>
          <button 
            onClick={() => {
              const element = document.getElementById('contact');
              if (element) {
                const offsetTop = element.offsetTop - 80;
                window.scrollTo({
                  top: offsetTop,
                  behavior: 'smooth'
                });
              }
            }}
            className="inline-flex items-center gap-2 bg-white text-blue-600 hover:bg-slate-100 px-8 py-4 text-lg rounded-md font-medium transition-colors"
          >
            Get In Touch
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      </section>
    </div>
  );
};

export default Projects;